import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Option } from "@/types/Shared/select";
import React, { useCallback } from "react";
import { useFormContext } from "react-hook-form";

type Props = React.ComponentProps<"input"> & {
  label?: string;
  name: string;
  options: Option[];
  labelClassName?: string;
  containerClassName?: string;
  textClassName?: string;
  onValueChange?: (value: string) => void;
  value?: string;
  defaultValue?: string;
  allowDeselect?: boolean;
};

export default function ControlledRadioGroup({
  label,
  name,
  options,
  labelClassName = "text-sm font-normal",
  containerClassName,
  textClassName,
  onValueChange,
  value,
  defaultValue,
  allowDeselect = false,
}: Props) {
  const { control } = useFormContext();

  const handleRadioClick = useCallback(
    (optionValue: string, currentValue: string) => {
      if (allowDeselect && currentValue === optionValue) {
        // 이미 선택된 항목을 클릭하면 해제
        return "";
      }
      return optionValue;
    },
    [allowDeselect]
  );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel className={labelClassName}>{label}</FormLabel>
          <FormControl>
            <RadioGroup
              onValueChange={(value) => {
                field.onChange(value);
                onValueChange?.(value);
              }}
              defaultValue={defaultValue}
              value={value || field.value}
              className={containerClassName}
            >
              {options.map((option) => (
                <FormItem
                  key={option.value}
                  className="flex items-center gap-1 space-y-0"
                >
                  <FormControl>
                    <RadioGroupItem
                      value={option.value}
                      onClick={(e) => {
                        if (allowDeselect) {
                          e.preventDefault();
                          const currentValue = value || field.value;
                          const newValue = handleRadioClick(
                            option.value,
                            currentValue
                          );
                          field.onChange(newValue);
                          onValueChange?.(newValue);
                        }
                      }}
                    />
                  </FormControl>
                  <FormLabel
                    className={textClassName}
                    onClick={(e) => {
                      if (allowDeselect) {
                        e.preventDefault();
                        const currentValue = value || field.value;
                        const newValue = handleRadioClick(
                          option.value,
                          currentValue
                        );
                        field.onChange(newValue);
                        onValueChange?.(newValue);
                      }
                    }}
                  >
                    {option.label}
                  </FormLabel>
                </FormItem>
              ))}
            </RadioGroup>
          </FormControl>
        </FormItem>
      )}
    />
  );
}
